import type { RecommendationContext } from "@memorang/types";
import { z } from "zod";

const masteryValue = z.number().finite().min(0).max(100).nullable();
const axisValue = z.object({
	id: z.number().int().positive(),
	title: z.string().min(1),
	sortOrder: z.number().int(),
});
const axis = z.object({
	id: z.string().min(1),
	title: z.string().min(1),
	role: z.enum(["TRACK", "PROGRESSION", "DIMENSION"]),
	values: z.array(axisValue).min(1),
});
const coordinate = z.object({
	axisId: z.string().min(1),
	valueId: z.number().int().positive(),
});
const distribution = z.object({
	none: z.number().int().nonnegative(),
	low: z.number().int().nonnegative(),
	medium: z.number().int().nonnegative(),
	high: z.number().int().nonnegative(),
});
const cell = z.object({
	key: z.string().min(1),
	coordinates: z.array(coordinate).min(1),
	launch: z.object({
		availableItemCount: z.number().int().nonnegative(),
		distribution,
		tagGroups: z.array(z.array(z.number().int().positive()).min(1)),
	}),
	evidence: z.object({
		totalResponses: z.number().int().nonnegative(),
		distinctItemCount: z.number().int().nonnegative(),
		lastAnsweredAt: z.iso.datetime().nullable(),
		rollingMastery10: masteryValue,
		rollingMastery20: masteryValue,
		rollingMastery30: masteryValue,
	}),
});

const baseContextSchema = z.object({
	schemaVersion: z.literal(2),
	calculationVersion: z.literal(2),
	generatedAt: z.iso.datetime(),
	exam: z.object({
		id: z.string().min(1),
		numericId: z.number().int().positive(),
		title: z.string().min(1),
	}),
	axes: z.array(axis).min(2),
	policy: z.object({
		rollingWindows: z.tuple([z.literal(10), z.literal(20), z.literal(30)]),
		minimumEvidence: z.number().int().positive(),
		passingMastery: z.number().min(0).max(100),
		rollupAxisSets: z.array(z.array(z.string().min(1)).min(1)),
		targetBarrier: z.object({ requiresAllTracks: z.literal(true) }),
	}),
	cells: z.array(cell),
});

type ParsedContext = z.infer<typeof baseContextSchema>;

const addIssue = (refinement: z.RefinementCtx, message: string): void => {
	refinement.addIssue({ code: "custom", message });
};

const validateAxes = (
	context: ParsedContext,
	refinement: z.RefinementCtx,
): Map<string, ParsedContext["axes"][number]> => {
	const axes = new Map(context.axes.map((entry) => [entry.id, entry]));
	if (axes.size !== context.axes.length) {
		addIssue(refinement, "Axis IDs must be unique");
	}
	if (context.axes.filter(({ role }) => role === "PROGRESSION").length !== 1) {
		addIssue(refinement, "Exactly one progression axis is required");
	}
	if (!context.axes.some(({ role }) => role === "TRACK")) {
		addIssue(refinement, "A track axis is required");
	}
	for (const entry of context.axes) {
		if (
			new Set(entry.values.map(({ id }) => id)).size !== entry.values.length
		) {
			refinement.addIssue({
				code: "custom",
				message: `Axis ${entry.id} values must be unique`,
			});
		}
	}
	return axes;
};

const validateCells = (
	context: ParsedContext,
	axes: Map<string, ParsedContext["axes"][number]>,
	refinement: z.RefinementCtx,
): void => {
	for (const entry of context.cells) {
		const coordinateAxes = new Set<string>();
		for (const value of entry.coordinates) {
			const configuredAxis = axes.get(value.axisId);
			if (
				!configuredAxis ||
				coordinateAxes.has(value.axisId) ||
				!configuredAxis.values.some(({ id }) => id === value.valueId)
			) {
				refinement.addIssue({
					code: "custom",
					message: `Cell ${entry.key} has invalid coordinates`,
				});
			}
			coordinateAxes.add(value.axisId);
		}
		const launchTotal = Object.values(entry.launch.distribution).reduce(
			(sum, count) => sum + count,
			0,
		);
		if (launchTotal !== entry.launch.availableItemCount) {
			addIssue(refinement, `Cell ${entry.key} has inconsistent launch counts`);
		}
	}
	const cellKeys = context.cells.map(({ key }) => key);
	if (new Set(cellKeys).size !== cellKeys.length) {
		addIssue(refinement, "Cell keys must be unique");
	}
};

const validateRollups = (
	context: ParsedContext,
	refinement: z.RefinementCtx,
): void => {
	const axisIds = new Set(context.axes.map(({ id }) => id));
	const rollups = context.policy.rollupAxisSets.map((axisSet) => {
		if (
			new Set(axisSet).size !== axisSet.length ||
			axisSet.some((axisId) => !axisIds.has(axisId))
		) {
			addIssue(refinement, "Rollups must contain unique configured axes");
		}
		return [...axisSet].sort().join("|");
	});
	if (new Set(rollups).size !== rollups.length) {
		addIssue(refinement, "Rollups must be unique");
	}
};

const contextSchema = baseContextSchema.superRefine((context, refinement) => {
	const axes = validateAxes(context, refinement);
	validateCells(context, axes, refinement);
	validateRollups(context, refinement);
});

export const parseRecommendationContext = (
	input: unknown,
): RecommendationContext => {
	const parsed = contextSchema.safeParse(input);
	if (!parsed.success) {
		throw new Error(
			`Recommendation context is invalid: ${z.prettifyError(parsed.error)}`,
		);
	}
	return parsed.data;
};
